"use client"

import type React from "react"
import { useState, useEffect } from "react"
import { Card, Typography, Button, Grid, Box, IconButton, Skeleton, CircularProgress, Chip, Avatar } from "@mui/material"
import { motion } from "framer-motion"
import { IonPage, IonHeader, IonToolbar, IonTitle, IonContent, IonButtons, IonButton, IonIcon, useIonViewWillEnter } from "@ionic/react"
import { refreshOutline } from "ionicons/icons"
import {
  TrendingUp,
  TrendingDown,
  People,
  Work,
  Assignment,
  Payment,
  Schedule,
  Diversity3,
  Analytics,
  Notifications,
  Settings,
  PersonAdd,
  GroupAdd,
  Assessment,
  EmojiPeople,
} from "@mui/icons-material"
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  AreaChart,
  Area,
} from "recharts"
import axios from "axios"
import { requestID, baseUrl, config } from "../../common/utils"
import { formatCurrencyN } from "../../common/utils_t"

// Mock data for HR dashboard
const recruitmentData = [
  { month: "Jan", applications: 120, interviews: 45, hires: 8 },
  { month: "Feb", applications: 98, interviews: 38, hires: 7 },
  { month: "Mar", applications: 145, interviews: 52, hires: 10 },
  { month: "Apr", applications: 132, interviews: 48, hires: 9 },
  { month: "May", applications: 167, interviews: 61, hires: 12 },
  { month: "Jun", applications: 189, interviews: 72, hires: 15 },
]

const departmentData = [
  { name: "Engineering", employees: 45, openPositions: 5, color: "#8884d8" },
  { name: "Marketing", employees: 32, openPositions: 3, color: "#82ca9d" },
  { name: "Sales", employees: 28, openPositions: 7, color: "#ffc658" },
  { name: "HR", employees: 15, openPositions: 2, color: "#ff7300" },
  { name: "Finance", employees: 22, openPositions: 4, color: "#00ff88" },
]

const employeeSatisfaction = [
  { quarter: "Q1", satisfaction: 78, retention: 92 },
  { quarter: "Q2", satisfaction: 82, retention: 94 },
  { quarter: "Q3", satisfaction: 85, retention: 96 },
  { quarter: "Q4", satisfaction: 88, retention: 97 },
]

const upcomingEvents = [
  { id: 1, type: "Interview", title: "Senior Developer Interview", time: "10:00 AM", participants: "John Smith, Maria Garcia" },
  { id: 2, type: "Training", title: "Leadership Workshop", time: "2:00 PM", participants: "All Managers" },
  { id: 3, type: "Review", title: "Q2 Performance Reviews", time: "4:00 PM", participants: "HR Team" },
]

const recentActivities = [
  { id: 1, action: "Hired", person: "Sarah Johnson", position: "UX Designer", time: "2 hours ago" },
  { id: 2, action: "Review", person: "Michael Chen", position: "Product Manager", time: "1 day ago" },
  { id: 3, action: "Promoted", person: "Emily Wilson", position: "Senior Developer", time: "2 days ago" },
  { id: 4, action: "Onboarding", person: "James Brown", position: "Marketing Specialist", time: "3 days ago" },
]

interface UserData {
  firstName: string
  middleName: string
  lastName: string
}

const HrConsultingPage: React.FC = () => {
  const [userData, setUserData] = useState<UserData | null>({ firstName: "HR", middleName: "", lastName: "Manager" })
  const [loading, setLoading] = useState(false)
  const [lastRefresh, setLastRefresh] = useState(new Date())
  const [selectedTimeframe, setSelectedTimeframe] = useState("7d")
  const [loaddata, setLoadData] = useState(false)

  // HR-specific state
  const [totalEmployees, setTotalEmployees] = useState(0)
  const [openPositions, setOpenPositions] = useState(0)
  const [interviewSchedule, setInterviewSchedule] = useState(0)
  const [satisfactionRate, setSatisfactionRate] = useState(0)
  const [upcomingInterviews, setUpcomingInterviews] = useState<any[]>([])
  const [hrActivities, setHrActivities] = useState<any[]>([])

  useEffect(() => {
    // Simulate loading user data
    const storedData = localStorage.getItem("userData")
    if (storedData) {
      setUserData(JSON.parse(storedData))
    }
  }, [])

  const handleRefresh = () => {
    setLoading(true)
    setTimeout(() => {
      setLastRefresh(new Date())
      setLoading(false)
    }, 1000)
  }

  const quickMenuItems = [
    { icon: <People />, label: "Employees", color: "#1976d2", count: null },
    { icon: <Work />, label: "Positions", color: "#388e3c", count: "12" },
    { icon: <Assignment />, label: "Recruitment", color: "#f57c00", count: "24" },
    { icon: <Payment />, label: "Payroll", color: "#7b1fa2", count: null },
    { icon: <Schedule />, label: "Schedule", color: "#d32f2f", count: "8" },
    { icon: <Diversity3 />, label: "Teams", color: "#0288d1", count: null },
    { icon: <Notifications />, label: "Alerts", color: "#e91e63", count: "3" },
    { icon: <Settings />, label: "Settings", color: "#795548", count: null },
  ]

  const timeframeOptions = [
    { value: "24h", label: "24 Hours" },
    { value: "7d", label: "7 Days" },
    { value: "30d", label: "30 Days" },
    { value: "90d", label: "90 Days" },
  ]

  const statusColors = {
    completed: "success",
    pending: "warning",
    rejected: "error",
    scheduled: "info"
  }

  // Make the function async
  const getHrDashboard = async () => {
    try {
      setLoadData(false)
      const formData = new FormData()
      formData.append("requestID", requestID())

      // This would be your actual API endpoint for HR data
      const response = await axios.post(
        baseUrl() + "hr/getDashboardInfo",
        formData,
        config
      )

      const data = response.data
      setTotalEmployees(data.total_employees || 122)
      setOpenPositions(data.open_positions || 12)
      setInterviewSchedule(data.interviews_scheduled || 8)
      setSatisfactionRate(data.satisfaction_rate || 88)
      setUpcomingInterviews(data.upcoming_interviews || [])
      setHrActivities(data.hr_activities || [])
      setLoadData(true)
    } catch (error) {
      console.error("Error fetching HR dashboard:", error)
      // Fallback to mock data if API fails
      setTotalEmployees(122)
      setOpenPositions(12)
      setInterviewSchedule(8)
      setSatisfactionRate(88)
      setUpcomingInterviews(upcomingEvents)
      setHrActivities(recentActivities)
      setLoadData(true)
    }
  }

  useIonViewWillEnter(() => {
    getHrDashboard()
  }, [])

  return (
    <IonPage className="pushDown">
      <IonHeader>
        <IonToolbar color="primary">
          <IonTitle>👥 HR Consulting Dashboard - Welcome, {userData ? `${userData.firstName}` : "HR Manager"}!</IonTitle>
          <IonButtons slot="end">
            <Typography variant="caption" style={{ marginRight: "10px", color: "white" }}>
              Last updated: {lastRefresh.toLocaleTimeString()}
            </Typography>
            <IonButton fill="clear" onClick={handleRefresh} disabled={loading}>
              <IonIcon
                icon={refreshOutline}
                style={{
                  animation: loading ? "spin 1s linear infinite" : "none",
                  color: "white",
                }}
              />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent fullscreen>
        <Box
          sx={{
            padding: "20px",
            maxWidth: "1400px",
            margin: "0 auto",
            paddingBottom: "40px",
          }}
        >
          {/* Quick Menu Section */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <Typography variant="h5" fontWeight="bold" sx={{ mb: 2, color: "#1976d2" }}>
              🚀 Quick Actions
            </Typography>
            <Grid container spacing={2} sx={{ mb: 4 }}>
              {quickMenuItems.map((item, index) => (
                <Grid item xs={6} sm={4} md={3} lg={1.5} key={index}>
                  <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                    <Card
                      sx={{
                        p: 2,
                        textAlign: "center",
                        cursor: "pointer",
                        backgroundColor: item.color,
                        color: "white",
                        position: "relative",
                        "&:hover": { opacity: 0.9 },
                        minHeight: "80px",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                      }}
                    >
                      {item.count && (
                        <Box
                          sx={{
                            position: "absolute",
                            top: -8,
                            right: -8,
                            backgroundColor: "#ff4444",
                            color: "white",
                            borderRadius: "50%",
                            width: 24,
                            height: 24,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "12px",
                            fontWeight: "bold",
                          }}
                        >
                          {item.count}
                        </Box>
                      )}
                      {item.icon}
                      <Typography variant="caption" display="block" sx={{ mt: 1, fontSize: "11px" }}>
                        {item.label}
                      </Typography>
                    </Card>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </motion.div>

          {/* Key HR Metrics Cards */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <Typography variant="h5" fontWeight="bold" sx={{ mb: 2, color: "#1976d2" }}>
              📊 HR Metrics
            </Typography>
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ p: 3, backgroundColor: "#e3f2fd", borderLeft: "5px solid #1976d2" }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      {loaddata ? (
                        <Typography variant="h4" fontWeight="bold" color="#1976d2">
                          {totalEmployees}
                        </Typography>
                      ) : (
                        <Skeleton variant="text" width={80} height={50} />
                      )}
                      <Typography variant="body2" color="textSecondary">
                        Total Employees
                      </Typography>
                    </Box>
                    <People sx={{ fontSize: 50, color: "#1976d2", opacity: 0.7 }} />
                  </Box>
                  <Box display="flex" alignItems="center" mt={2}>
                    <TrendingUp sx={{ color: "green", mr: 1 }} />
                    <Typography variant="caption" color="green" fontWeight="bold">
                      +5% from last quarter
                    </Typography>
                  </Box>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ p: 3, backgroundColor: "#e8f5e8", borderLeft: "5px solid #388e3c" }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      {loaddata ? (
                        <Typography variant="h4" fontWeight="bold" color="#388e3c">
                          {openPositions}
                        </Typography>
                      ) : (
                        <Skeleton variant="text" width={80} height={50} />
                      )}
                      <Typography variant="body2" color="textSecondary">
                        Open Positions
                      </Typography>
                    </Box>
                    <Work sx={{ fontSize: 50, color: "#388e3c", opacity: 0.7 }} />
                  </Box>
                  <Box display="flex" alignItems="center" mt={2}>
                    <TrendingDown sx={{ color: "red", mr: 1 }} />
                    <Typography variant="caption" color="red" fontWeight="bold">
                      -2 from last month
                    </Typography>
                  </Box>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ p: 3, backgroundColor: "#fff3e0", borderLeft: "5px solid #f57c00" }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      {loaddata ? (
                        <Typography variant="h4" fontWeight="bold" color="#f57c00">
                          {interviewSchedule}
                        </Typography>
                      ) : (
                        <Skeleton variant="text" width={80} height={50} />
                      )}
                      <Typography variant="body2" color="textSecondary">
                        Interviews Scheduled
                      </Typography>
                    </Box>
                    <Schedule sx={{ fontSize: 50, color: "#f57c00", opacity: 0.7 }} />
                  </Box>
                  <Box display="flex" alignItems="center" mt={2}>
                    <TrendingUp sx={{ color: "green", mr: 1 }} />
                    <Typography variant="caption" color="green" fontWeight="bold">
                      +3 this week
                    </Typography>
                  </Box>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ p: 3, backgroundColor: "#f3e5f5", borderLeft: "5px solid #7b1fa2" }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      {loaddata ? (
                        <Typography variant="h4" fontWeight="bold" color="#7b1fa2">
                          {satisfactionRate}%
                        </Typography>
                      ) : (
                        <Skeleton variant="text" width={80} height={50} />
                      )}
                      <Typography variant="body2" color="textSecondary">
                        Employee Satisfaction
                      </Typography>
                    </Box>
                    <EmojiPeople sx={{ fontSize: 50, color: "#7b1fa2", opacity: 0.7 }} />
                  </Box>
                  <Box display="flex" alignItems="center" mt={2}>
                    <TrendingUp sx={{ color: "green", mr: 1 }} />
                    <Typography variant="caption" color="green" fontWeight="bold">
                      +5% from last quarter
                    </Typography>
                  </Box>
                </Card>
              </Grid>
            </Grid>
          </motion.div>

          {/* Charts Section */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {/* Recruitment Trend Chart */}
            <Grid item xs={12} lg={8}>
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                <Card sx={{ p: 3, backgroundColor: "white", boxShadow: "0 4px 20px rgba(0,0,0,0.1)" }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                    <Typography variant="h6" fontWeight="bold" color="#1976d2">
                      📈 Recruitment Trends (6 Months)
                    </Typography>
                    <IconButton onClick={handleRefresh} size="small">
                      <IonIcon icon={refreshOutline} />
                    </IconButton>
                  </Box>
                  <ResponsiveContainer width="100%" height={350}>
                    <BarChart data={recruitmentData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="month" stroke="#666" />
                      <YAxis stroke="#666" />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "white",
                          border: "1px solid #ddd",
                          borderRadius: "8px",
                        }}
                      />
                      <Legend />
                      <Bar dataKey="applications" fill="#8884d8" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="interviews" fill="#82ca9d" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="hires" fill="#ffc658" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Card>
              </motion.div>
            </Grid>

            {/* Department Distribution */}
            <Grid item xs={12} lg={4}>
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.5 }}
              >
                <Card sx={{ p: 3, backgroundColor: "white", boxShadow: "0 4px 20px rgba(0,0,0,0.1)" }}>
                  <Typography variant="h6" fontWeight="bold" color="#1976d2" mb={3}>
                    🏢 Employees by Department
                  </Typography>
                  <ResponsiveContainer width="100%" height={350}>
                    <PieChart>
                      <Pie
                        data={departmentData}
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="employees"
                        label={({ name, value }) => `${name}: ${value}`}
                      >
                        {departmentData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </Card>
              </motion.div>
            </Grid>

            {/* Employee Satisfaction */}
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
              >
                <Card sx={{ p: 3, backgroundColor: "white", boxShadow: "0 4px 20px rgba(0,0,0,0.1)" }}>
                  <Typography variant="h6" fontWeight="bold" color="#1976d2" mb={3}>
                    😊 Employee Satisfaction & Retention
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={employeeSatisfaction}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="quarter" stroke="#666" />
                      <YAxis stroke="#666" />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "white",
                          border: "1px solid #ddd",
                          borderRadius: "8px",
                        }}
                      />
                      <Legend />
                      <Area
                        type="monotone"
                        dataKey="satisfaction"
                        stackId="1"
                        stroke="#8884d8"
                        fill="#8884d8"
                        fillOpacity={0.6}
                      />
                      <Area
                        type="monotone"
                        dataKey="retention"
                        stackId="1"
                        stroke="#82ca9d"
                        fill="#82ca9d"
                        fillOpacity={0.6}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </Card>
              </motion.div>
            </Grid>

            {/* Open Positions by Department */}
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.7 }}
              >
                <Card sx={{ p: 3, backgroundColor: "white", boxShadow: "0 4px 20px rgba(0,0,0,0.1)" }}>
                  <Typography variant="h6" fontWeight="bold" color="#1976d2" mb={3}>
                    📋 Open Positions by Department
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={departmentData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="name" stroke="#666" />
                      <YAxis stroke="#666" />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "white",
                          border: "1px solid #ddd",
                          borderRadius: "8px",
                        }}
                      />
                      <Legend />
                      <Bar dataKey="openPositions" fill="#8884d8" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Card>
              </motion.div>
            </Grid>
          </Grid>

          {/* Upcoming Events & Recent Activities */}
          <Grid container spacing={3}>
            {/* Upcoming Events */}
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.8 }}
              >
                <Card sx={{ p: 3, backgroundColor: "white", boxShadow: "0 4px 20px rgba(0,0,0,0.1)" }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                    <Typography variant="h6" fontWeight="bold" color="#1976d2">
                      📅 Upcoming Events
                    </Typography>
                    <Button variant="outlined" size="small" startIcon={<Schedule />}>
                      View Calendar
                    </Button>
                  </Box>
                  <Box>
                    {upcomingInterviews.map((event, index) => (
                      <motion.div
                        key={event.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.3, delay: index * 0.1 }}
                        whileHover={{ backgroundColor: "#f8f9fa" }}
                        style={{ padding: "16px", borderRadius: "12px", marginBottom: "12px", border: "1px solid #eee" }}
                      >
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                          <Box display="flex" alignItems="flex-start">
                            <Box
                              sx={{
                                width: 48,
                                height: 48,
                                borderRadius: "50%",
                                backgroundColor: 
                                  event.type === "Interview" ? "#e8f5e8" : 
                                  event.type === "Training" ? "#e3f2fd" : "#fff3e0",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                mr: 3,
                              }}
                            >
                              {event.type === "Interview" ? (
                                <PersonAdd sx={{ color: "#388e3c", fontSize: 24 }} />
                              ) : event.type === "Training" ? (
                                <GroupAdd sx={{ color: "#1976d2", fontSize: 24 }} />
                              ) : (
                                <Assessment sx={{ color: "#f57c00", fontSize: 24 }} />
                              )}
                            </Box>
                            <Box>
                              <Typography variant="body1" fontWeight="bold">
                                {event.title}
                              </Typography>
                              <Typography variant="caption" display="block" color="textSecondary">
                                {event.time} • {event.participants}
                              </Typography>
                            </Box>
                          </Box>
                          <Chip 
                            label={event.type} 
                            size="small" 
                            color={
                              event.type === "Interview" ? "success" : 
                              event.type === "Training" ? "primary" : "warning"
                            } 
                          />
                        </Box>
                      </motion.div>
                    ))}
                  </Box>
                </Card>
              </motion.div>
            </Grid>

            {/* Recent HR Activities */}
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.9 }}
              >
                <Card sx={{ p: 3, backgroundColor: "white", boxShadow: "0 4px 20px rgba(0,0,0,0.1)" }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                    <Typography variant="h6" fontWeight="bold" color="#1976d2">
                      🔔 Recent HR Activities
                    </Typography>
                    <Button variant="outlined" size="small" startIcon={<Notifications />}>
                      View All
                    </Button>
                  </Box>
                  <Box>
                    {hrActivities.map((activity, index) => (
                      <motion.div
                        key={activity.id}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.3, delay: index * 0.1 }}
                        whileHover={{ backgroundColor: "#f8f9fa" }}
                        style={{ padding: "16px", borderRadius: "12px", marginBottom: "12px", border: "1px solid #eee" }}
                      >
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Box display="flex" alignItems="center">
                            <Avatar sx={{ width: 40, height: 40, mr: 2, bgcolor: 
                              activity.action === "Hired" ? "#e8f5e8" : 
                              activity.action === "Promoted" ? "#e3f2fd" : 
                              activity.action === "Review" ? "#fff3e0" : "#f3e5f5"
                            }}>
                              {activity.action === "Hired" ? (
                                <PersonAdd sx={{ color: "#388e3c" }} />
                              ) : activity.action === "Promoted" ? (
                                <TrendingUp sx={{ color: "#1976d2" }} />
                              ) : activity.action === "Review" ? (
                                <Assessment sx={{ color: "#f57c00" }} />
                              ) : (
                                <GroupAdd sx={{ color: "#7b1fa2" }} />
                              )}
                            </Avatar>
                            <Box>
                              <Typography variant="body1" fontWeight="bold">
                                {activity.person}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                {activity.position} • {activity.time}
                              </Typography>
                            </Box>
                          </Box>
                          <Chip 
                            label={activity.action} 
                            size="small" 
                            color={
                              activity.action === "Hired" ? "success" : 
                              activity.action === "Promoted" ? "primary" : 
                              activity.action === "Review" ? "warning" : "secondary"
                            } 
                          />
                        </Box>
                      </motion.div>
                    ))}
                  </Box>
                </Card>
              </motion.div>
            </Grid>
          </Grid>
        </Box>

        <style jsx>{`
          @keyframes spin {
            from {
              transform: rotate(0deg);
            }
            to {
              transform: rotate(360deg);
            }
          }
        `}</style>
      </IonContent>
    </IonPage>
  )
}

export default HrConsultingPage